import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IDataGenericText, IDataGrouped } from '@app/interfaces/idata-grouped';
import { environment } from '@env/environment';
import { status_code } from 'functions/src/help/status-query';
import { Observable, map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {

  private BASE_URL_SERVICE: string = environment.endpoint.apiconfig;

  private configs: { [key: string]: Array<IDataGenericText> } = {};

  constructor(private http: HttpClient) { }

  membershipList(_idEmployer) {

    return this.http.get(this.BASE_URL_SERVICE + `membershipList/${_idEmployer}`);
  }

  postSurvey(req: any) {
    return this.http.post<any>(this.BASE_URL_SERVICE + 'postSurvey/', req);
  }

  postSurveyCategory(req: any) {
    return this.http.post<any>(this.BASE_URL_SERVICE + 'postSurveyCategory/', req);
  }

  getConfiguration(arrConfigurarion: Array<ConfigurationType>) {
    return this.http.post<any>(this.BASE_URL_SERVICE + 'getConfigurationParam/', { configuration: arrConfigurarion });
  }

  getPositionCategory() {
    return this.http.get<any>(this.BASE_URL_SERVICE + 'getPositionCategory/');
  }

  getConfigurationList(configurationName: Array<ConfigurationType>): Observable<{ [key: string]: Array<IDataGenericText> }> {

    const configurationUnloaded: Array<ConfigurationType> = [];

    configurationName.forEach(element => {
      if (!this.configs[element]) {
        configurationUnloaded.push(element)
      }
    });

    if (configurationUnloaded.length === 0)
      return of(this.configs);

    return this.http.post<any>(this.BASE_URL_SERVICE + 'getConfigurationParam/', { configuration: configurationUnloaded }).pipe(
      tap(res => {

        if (res.status_code === status_code.success) {

          configurationUnloaded.forEach(element => {
            this.configs[element] = res?.data[element].map((e: { id: string | number; text: string; add: string | undefined; parent: number | undefined; }) => { return { id: +e.id, text: e.text, additional: e.add, parent: e.parent } })
          });

        }
      }),
      map(() => this.configs)
    );
  }

  public getLocalConfig(key: ConfigurationType): Array<IDataGenericText> {

    if (this.configs[key])
      return this.configs[key];

    return [];
  }

  getDate() {
    return this.http.get<any>(this.BASE_URL_SERVICE + 'date/');
  }

  getArrayGrouped(parentArray: Array<IDataGenericText>, childArray: Array<IDataGenericText>): Array<IDataGrouped> {

    return Object.freeze(parentArray.map(parent => {

      const items = childArray.filter(item => +item.parent === +parent.id).map(x => {
        return {
          id: x.id,
          child: x.text,
          additional: x.additional
        }
      })
      return { parent: parent.text, additional: parent.additional, items: items };
    })) as Array<IDataGrouped>;
  }

  // Métodos para WhatsApp Community usando el patrón genérico
  /**
   * Inicializa la carga del ID de la comunidad de WhatsApp usando el patrón genérico
   */
  initializeWhatsappCommunityConfig(): void {
    this.getConfigurationList(['whatsapp_community_id']).subscribe(
      (configs) => {
        const whatsappConfig = configs['whatsapp_community_id'];
        if (whatsappConfig && whatsappConfig.length > 0) {
          // El ID está en el campo 'text' del primer elemento
          const communityId = whatsappConfig[0].text;
          if (communityId) {
            // No necesitamos almacenar en variable separada, se mantiene en configs
            console.log('WhatsApp Community ID loaded:', communityId);
          } else {
            console.warn('WhatsApp Community ID not found in configuration, using default');
          }
        } else {
          console.warn('WhatsApp Community configuration not found, using default');
        }
      },
      (error) => {
        console.error('Error loading WhatsApp Community configuration:', error);
      }
    );
  }

  /**
   * Obtiene el ID de la comunidad de WhatsApp actual desde la configuración genérica
   */
  getWhatsappCommunityId(): string {
    const whatsappConfig = this.configs['whatsapp_community_id'];
    if (whatsappConfig && whatsappConfig.length > 0) {
      return whatsappConfig[0].text || '';
    }
    return ''; // ID vacío si no está configurado
  }

  /**
   * Obtiene la URL completa de la comunidad de WhatsApp
   */
  getWhatsappCommunityUrl(): string {
    const communityId = this.getWhatsappCommunityId();
    return `https://chat.whatsapp.com/${communityId}`;
  }

  // Temporada de cargos del próximo año
  /**
   * Carga la temporada del próximo año. El backend la resuelve contra la ventana
   * de fechas configurada, así que acá no hay años ni fechas escritas a mano.
   *
   * Devuelve la caché sin pedir nada al servidor si ya fue cargada, por lo que
   * los componentes pueden llamarla en su ngOnInit sin costo.
   */
  loadNextSeason(): Observable<{ enabled: boolean, year: number }> {
    return this.getConfigurationList(['job_next_season']).pipe(
      map(() => this.getNextSeason())
    );
  }

  /**
   * Estado de la temporada ya cargada. `enabled` viene en el id y el año en el text.
   */
  getNextSeason(): { enabled: boolean, year: number } {

    const config = this.getLocalConfig('job_next_season');

    if (!(config && config.length > 0)) {
      return { enabled: false, year: 0 };
    }

    return { enabled: +config[0].id === 1, year: +config[0].text || 0 };
  }

}

type ConfigurationType =
  'teaching_carrer_biennium'
  | 'teaching_carrer_section'
  | 'level_grade'
  | 'type_contract'
  | 'position'
  | 'position_category'
  | 'complaint_case_type'
  | 'complaint_protocol_stage'
  | 'whatsapp_community_id'
  | 'job_next_season';